import { SEMAPHORE } from "@thi.ng/api";
import { implementsFunction, isFunction, isString } from "@thi.ng/checks";
import { illegalArity, illegalState } from "@thi.ng/errors";
import { comp, isReduced, push, unreduced } from "@thi.ng/transducers";
import { LOGGER } from "./api";
import { nextID } from "./utils/idgen";
/**
 * Creates a new `Subscription` instance, the fundamental datatype &
 * building block provided by this package (`Stream`s are
 * `Subscription`s too). Subscriptions can be:
 *
 * - linked into directed graphs (if async, not necessarily DAGs)
 * - transformed using transducers (incl. early termination)
 * - can have any number of subscribers (optionally each w/ their own
 *   transducer)
 * - recursively unsubscribe themselves from parent after their last
 *   subscriber unsubscribed
 * - will go into a non-recoverable error state if NONE of the
 *   subscribers has an error handler itself
 * - implement the @thi.ng/api `IDeref` interface
 *
 * ```
 * // as reactive value mechanism (same as with stream() above)
 * s = rs.subscription();
 * s.subscribe(trace("s1"));
 * s.subscribe(trace("s2"), tx.filter((x) => x > 25));
 *
 * // external trigger
 * s.next(23);
 * // s1 23
 * s.next(42);
 * // s1 42
 * // s2 42
 * ```
 *
 * @param sub
 * @param xform
 * @param parent
 * @param id
 */
export const subscription = (sub, xform, parent, id) => new Subscription(sub, xform, parent, id);
export class Subscription {
    constructor(sub, xform, parent, id) {
        this.state = 0 /* IDLE */;
        this.parent = parent;
        this.id = id || `sub-${nextID()}`;
        this.last = SEMAPHORE;
        this.subs = [];
        if (sub) {
            this.subs.push(sub);
        }
        if (xform) {
            this.xform = xform(push());
        }
    }
    deref() {
        return this.last !== SEMAPHORE ? this.last : undefined;
    }
    getState() {
        return this.state;
    }
    subscribe(...args) {
        this.ensureState();
        let sub, xform, id;
        switch (args.length) {
            case 1:
            case 2:
                if (isFunction(args[0])) {
                    xform = args[0];
                    id = args[1] || `xform-${nextID()}`;
                }
                else {
                    sub = args[0];
                    if (isFunction(args[1])) {
                        xform = args[1];
                    }
                    else {
                        id = args[1];
                    }
                }
                break;
            case 3:
                [sub, xform, id] = args;
                break;
            default:
                illegalArity(args.length);
        }
        if (implementsFunction(sub, "subscribe")) {
            sub.parent = this;
        }
        else {
            sub = subscription(sub, xform, this, id);
        }
        if (this.last !== SEMAPHORE) {
            sub.next(this.last);
        }
        return this.addWrapped(sub);
    }
    /**
     * Returns array of new child subscriptions for all given
     * subscribers.
     *
     * @param subs
     */
    subscribeAll(...subs) {
        const wrapped = [];
        for (let s of subs) {
            wrapped.push(this.subscribe(s));
        }
        return wrapped;
    }
    transform(...xf) {
        const n = xf.length - 1;
        if (isString(xf[n])) {
            return this.subscribe(comp(...xf.slice(0, n)), xf[n]);
        }
        else {
            return this.subscribe(comp(...xf));
        }
    }
    /**
     * If called without arg, removes this subscription from parent (if
     * any), cleans up internal state and goes into DONE state. If
     * called with arg, removes the sub from internal pool and if no
     * other subs are remaining also cleans up itself and goes into DONE
     * state.
     *
     * @param sub
     */
    unsubscribe(sub) {
        LOGGER.debug(this.id, "unsub start", sub ? sub.id : "self");
        if (!sub) {
            let res = true;
            if (this.parent) {
                res = this.parent.unsubscribe(this);
            }
            this.state = 2 /* DONE */;
            this.cleanup();
            return res;
        }
        if (this.subs) {
            LOGGER.debug(this.id, "unsub child", sub.id);
            const idx = this.subs.indexOf(sub);
            if (idx >= 0) {
                this.subs.splice(idx, 1);
                if (!this.subs.length) {
                    this.unsubscribe();
                }
                return true;
            }
        }
        return false;
    }
    next(x) {
        if (this.state < 2 /* DONE */) {
            if (this.xform) {
                const acc = this.xform[2]([], x);
                const uacc = unreduced(acc);
                const n = uacc.length;
                for (let i = 0; i < n; i++) {
                    this.dispatch(uacc[i]);
                }
                if (isReduced(acc)) {
                    this.done();
                }
            }
            else {
                this.dispatch(x);
            }
        }
    }
    done() {
        LOGGER.debug(this.id, "done start");
        if (this.state < 2 /* DONE */) {
            if (this.xform) {
                const acc = this.xform[1]([]);
                const uacc = unreduced(acc);
                const n = uacc.length;
                for (let i = 0; i < n; i++) {
                    this.dispatch(uacc[i]);
                }
            }
            this.state = 2 /* DONE */;
            for (let s of [...this.subs]) {
                s.done && s.done();
            }
            this.unsubscribe();
            LOGGER.debug(this.id, "done");
        }
    }
    error(e) {
        this.state = 3 /* ERROR */;
        let notified = false;
        if (this.subs && this.subs.length) {
            for (let s of this.subs.slice()) {
                if (s.error) {
                    s.error(e);
                    notified = true;
                }
            }
        }
        if (!notified) {
            LOGGER.warn(this.id, "unhandled error:", e);
            if (this.parent) {
                LOGGER.debug(this.id, "unsubscribing...");
                this.unsubscribe();
                this.state = 3 /* ERROR */;
            }
        }
    }
    addWrapped(wrapped) {
        this.subs.push(wrapped);
        this.state = 1 /* ACTIVE */;
        return wrapped;
    }
    dispatch(x) {
        // LOGGER.debug(this.id, "dispatch", x);
        this.last = x;
        const subs = this.subs;
        let s;
        if (subs.length == 1) {
            s = subs[0];
            try {
                s.next && s.next(x);
            }
            catch (e) {
                s.error ? s.error(e) : this.error(e);
            }
        }
        else {
            for (let i = subs.length - 1; i >= 0; i--) {
                s = subs[i];
                try {
                    s.next && s.next(x);
                }
                catch (e) {
                    s.error ? s.error(e) : this.error(e);
                }
            }
        }
    }
    ensureState() {
        if (this.state >= 2 /* DONE */) {
            illegalState(`operation not allowed in state ${this.state}`);
        }
    }
    cleanup() {
        LOGGER.debug(this.id, "cleanup");
        this.subs.length = 0;
        delete this.parent;
        delete this.xform;
        delete this.last;
    }
}
